import { useState, useEffect, useCallback } from 'react'
import { User, Building2, Bell, Shield, ShieldCheck, Mail, Save, Upload, Loader2, X, ImagePlus, Copy, Check, Sun, Moon, Monitor, RefreshCw, AlertTriangle, Play, Pause, Trash2, Database, Zap, FileLock, HardDrive, Bug, ShieldAlert, Activity } from 'lucide-react'
import { Card, Button, Input } from '../atoms'
import { cn } from '../atoms'
import { useCompany } from '../../hooks/useCompany'
import { useCompanyUsers } from '../../hooks/useCompanyUsers'
import { useCreateUser, useUpdateUser, useToggleUserStatus, useAdminResetPassword } from '../../hooks/useUsers'
import { TwoFactorSection } from '../organisms/TwoFactorSection'
import { AccountActivitySection } from '../organisms/AccountActivitySection'
import { useApi } from '../../store/auth.store'
import { useAuth } from '../../store/auth.store'
import { useTheme } from '../../store/theme.store'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'
import { useQueryClient } from '@tanstack/react-query'
import { getAssetUrl } from '../../utils/assetUrl'

const baseSections = [
  { id: 'profile', name: 'Perfil', icon: User },
  { id: 'appearance', name: 'Apariencia', icon: Sun },
  { id: 'company', name: 'Empresa', icon: Building2 },
  { id: 'notifications', name: 'Notificaciones', icon: Bell },
  { id: 'security', name: 'Seguridad', icon: Shield },
  { id: 'email', name: 'Correo', icon: Mail },
]

const adminSections = [
  { id: 'users', name: 'Usuarios', icon: User },
  { id: 'activity', name: 'Actividad de Cuentas', icon: Activity },
  { id: 'mailboxes', name: 'Mailboxes', icon: Mail },
  { id: 'attachments', name: 'Adjuntos', icon: FileLock },
  { id: 'reclassify', name: 'Re-clasificar', icon: RefreshCw },
  { id: 'data-cleanup', name: 'Limpiar Datos', icon: Trash2 },
]

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

export function SettingsPage() {
  const [activeSection, setActiveSection] = useState('profile')
  const { data: company, isLoading } = useCompany()
  const { user, updateUser: updateAuthUser } = useAuth()
  const api = useApi()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()
  const qc = useQueryClient()

  const isAdmin = user?.role === 'ADMIN'
  const userRole = user?.role || 'desconocido'
  const sections = isAdmin ? [...baseSections, ...adminSections] : baseSections

  const { data: companyUsers, isLoading: isLoadingUsers } = useCompanyUsers()
  const [areas, setAreas] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    if (activeSection === 'users') {
      api.get('/areas').then((res: any) => setAreas(res || [])).catch(() => setAreas([]))
    }
  }, [activeSection])

  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        avatarUrl: user.avatarUrl || '',
      })
    }
  }, [user])

  const [companyForm, setCompanyForm] = useState({
    name: '',
    nit: '',
    businessName: '',
    logoUrl: '',
    mainContact: '',
    mainEmail: '',
    phone: '',
  })

  // Estados para cambio de contraseña
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Estados para perfil de usuario
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    avatarUrl: user?.avatarUrl || '',
  })
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  // Estados para gestión de usuarios
  const [showUserModal, setShowUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState<any>(null)
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'OPERATOR',
    areaId: '',
  })

  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const toggleStatus = useToggleUserStatus()
  const adminResetPassword = useAdminResetPassword()
  const [resetPasswordUser, setResetPasswordUser] = useState<{ id: string; email: string; firstName: string; lastName: string } | null>(null)
  const [resetResult, setResetResult] = useState<{ tempPassword?: string; devTempPassword?: string; emailSent: boolean; mustChangePassword: boolean } | null>(null)
  const [resetCopied, setResetCopied] = useState(false)

  // Estados para gestión de mailboxes
  const [mailboxes, setMailboxes] = useState<any[]>([])
  const [isLoadingMailboxes, setIsLoadingMailboxes] = useState(false)
  const [showMailboxModal, setShowMailboxModal] = useState(false)
  const [editingMailbox, setEditingMailbox] = useState<any>(null)
  const [mailboxForm, setMailboxForm] = useState({
    address: '',
    email: '',
    name: '',
    department: '',
    provider: 'GMAIL',
    appPassword: '',
    imapHost: 'imap.zoho.com',
    imapPort: '993',
    smtpHost: 'smtp.zoho.com',
    smtpPort: '465',
  })

  const loadMailboxes = async () => {
    setIsLoadingMailboxes(true)
    try {
      const res: any = await api.get('/mailboxes')
      setMailboxes(res.mailboxes || [])
    } catch (err: any) {
      errorToast(err.message || 'Error cargando mailboxes')
    } finally {
      setIsLoadingMailboxes(false)
    }
  }

  useEffect(() => {
    if (activeSection === 'mailboxes') {
      loadMailboxes()
    }
  }, [activeSection])

  useEffect(() => {
    if (company) {
      setCompanyForm({
        name: company.name || '',
        nit: company.nit || '',
        businessName: company.businessName || '',
        logoUrl: company.logoUrl || '',
        mainContact: company.mainContact || '',
        mainEmail: company.mainEmail || '',
        phone: company.phone || '',
      })
    }
  }, [company])
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    console.log('📤 Subiendo logo...', file.name, file.type, file.size)
    console.log('👤 Usuario:', user?.email, 'Rol:', user?.role)

    const formData = new FormData()
    formData.append('file', file)

    setUploadingLogo(true)
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/companies/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      })

      if (response.ok) {
        const data = await response.json()
        setCompanyForm(prev => ({ ...prev, logoUrl: data.url }))
        setLastError(null)
        successToast('Logo subido correctamente')
        qc.invalidateQueries({ queryKey: ['company'] })
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Error al subir el logo' }))
        const msg = errorData.message || `Error ${response.status}: ${response.statusText}`
        setLastError(msg)
        errorToast(msg)
      }
    } catch (err: any) {
      const msg = err.message || 'Error al subir el logo'
      setLastError(msg)
      errorToast(msg)
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    setUploadingAvatar(true)
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/auth/users/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      })

      if (response.ok) {
        const data = await response.json()
        setUserForm(prev => ({ ...prev, avatarUrl: data.url }))
        updateAuthUser({ avatarUrl: data.url })
        successToast('Foto de perfil actualizada')
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Error al subir la foto' }))
        errorToast(errorData.message || 'Error al subir la foto')
      }
    } catch (err: any) {
      errorToast(err.message || 'Error al subir la foto')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errorToast('Las contraseñas no coinciden')
      return
    }
    if (passwordForm.newPassword.length < 8) {
      errorToast('La nueva contraseña debe tener al menos 8 caracteres')
      return
    }

    setIsChangingPassword(true)
    try {
      await api.patch('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      })
      successToast('Contraseña actualizada exitosamente')
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setLastError(null)
    } catch (err: any) {
      const msg = err.message || 'Error al cambiar contraseña'
      setLastError(msg)
      errorToast(msg)
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleSaveCompany = async () => {
    if (!company?.id) {
      errorToast('No se encontró la empresa')
      return
    }
    try {
      // Solo enviar campos que tienen valor para no sobreescribir con strings vacíos
      const dataToSend: any = {}
      if (companyForm.name !== undefined && companyForm.name !== '') dataToSend.name = companyForm.name
      if (companyForm.nit !== undefined && companyForm.nit !== '') dataToSend.nit = companyForm.nit
      if (companyForm.businessName !== undefined && companyForm.businessName !== '') dataToSend.businessName = companyForm.businessName
      if (companyForm.logoUrl !== undefined && companyForm.logoUrl !== '') dataToSend.logoUrl = companyForm.logoUrl
      if (companyForm.mainContact !== undefined && companyForm.mainContact !== '') dataToSend.mainContact = companyForm.mainContact
      if (companyForm.mainEmail !== undefined && companyForm.mainEmail !== '') dataToSend.mainEmail = companyForm.mainEmail
      if (companyForm.phone !== undefined && companyForm.phone !== '') dataToSend.phone = companyForm.phone

      console.log('📝 Guardando datos:', dataToSend)
      console.log('👤 Usuario:', user?.email, 'Rol:', user?.role)

      await api.patch(`/companies/me`, dataToSend)
      setLastError(null)
      successToast('Datos de empresa guardados')
      qc.invalidateQueries({ queryKey: ['company'] })
    } catch (err: any) {
      const msg = err.message || 'Error al guardar'
      console.error('❌ Error guardando:', msg)
      setLastError(msg)
      errorToast(msg)
    }
  }

  // Manejadores para usuarios
  const handleOpenUserModal = (user: any = null) => {
    if (user) {
      setEditingUser(user)
      setUserForm({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        areaId: user.area?.id || '',
      })
    } else {
      setEditingUser(null)
      setUserForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'OPERATOR',
        areaId: '',
      })
    }
    setShowUserModal(true)
    setLastError(null)
  }

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        // Actualizar usuario existente
        await updateUser.mutateAsync({
          id: editingUser.id,
          data: {
            firstName: userForm.firstName,
            lastName: userForm.lastName,
            role: userForm.role,
            areaId: userForm.areaId || undefined,
          },
        })
        successToast('Usuario actualizado')
      } else {
        // Crear nuevo usuario
        if (!userForm.password || userForm.password.length < 8) {
          errorToast('La contraseña debe tener al menos 8 caracteres')
          return
        }
        await createUser.mutateAsync({
          email: userForm.email,
          password: userForm.password,
          firstName: userForm.firstName,
          lastName: userForm.lastName,
          role: userForm.role,
          areaId: userForm.areaId || undefined,
        })
        successToast('Usuario creado exitosamente')
      }
      setShowUserModal(false)
      setLastError(null)
    } catch (err: any) {
      const msg = err.message || 'Error al guardar usuario'
      setLastError(msg)
      errorToast(msg)
    }
  }

  const handleToggleStatus = async (userId: string) => {
    try {
      await toggleStatus.mutateAsync(userId)
      successToast('Estado del usuario actualizado')
    } catch (err: any) {
      errorToast(err.message || 'Error al cambiar estado')
    }
  }

  const handleOpenResetPassword = (u: { id: string; email: string; firstName: string; lastName: string }) => {
    setResetPasswordUser(u)
    setResetResult(null)
  }

  const handleCloseResetPassword = () => {
    setResetPasswordUser(null)
    setResetResult(null)
    setResetCopied(false)
  }

  const handleConfirmResetPassword = async () => {
    if (!resetPasswordUser) return
    try {
      const result = await adminResetPassword.mutateAsync({ id: resetPasswordUser.id, data: {} })
      setResetResult({
        tempPassword: result.tempPassword,
        devTempPassword: result.devTempPassword,
        emailSent: result.emailSent,
        mustChangePassword: result.mustChangePassword,
      })
      if (result.emailSent) {
        successToast('Contraseña restablecida', `Se envió un email a ${resetPasswordUser.email}`)
      } else {
        successToast('Contraseña restablecida', 'Copia la contraseña temporal y compártela con el usuario')
      }
    } catch (err: any) {
      errorToast(err.message || 'Error al restablecer contraseña')
    }
  }

  const handleCopyTempPassword = async (pw: string) => {
    try {
      await navigator.clipboard.writeText(pw)
      setResetCopied(true)
      setTimeout(() => setResetCopied(false), 2000)
    } catch {
      errorToast('No se pudo copiar al portapapeles')
    }
  }

  // Handlers para mailboxes
  const handleOpenMailboxModal = (mb: any = null) => {
    if (mb) {
      setEditingMailbox(mb)
      const config = typeof mb.providerConfig === 'string' ? JSON.parse(mb.providerConfig || '{}') : (mb.providerConfig || {})
      setMailboxForm({
        address: mb.address,
        email: mb.email || '',
        name: mb.name,
        department: mb.department || '',
        provider: mb.provider || 'GMAIL',
        appPassword: config.appPassword || '',
        imapHost: config.imapHost || 'imap.zoho.com',
        imapPort: String(config.imapPort || '993'),
        smtpHost: config.smtpHost || 'smtp.zoho.com',
        smtpPort: String(config.smtpPort || '465'),
      })
    } else {
      setEditingMailbox(null)
      setMailboxForm({
        address: '',
        email: '',
        name: '',
        department: '',
        provider: 'GMAIL',
        appPassword: '',
        imapHost: 'imap.zoho.com',
        imapPort: '993',
        smtpHost: 'smtp.zoho.com',
        smtpPort: '465',
      })
    }
    setShowMailboxModal(true)
    setLastError(null)
  }

  const handleSaveMailbox = async () => {
    try {
      const providerConfig = mailboxForm.provider === 'ZOHO' ? {
        appPassword: mailboxForm.appPassword,
        imapHost: mailboxForm.imapHost,
        imapPort: parseInt(mailboxForm.imapPort) || 993,
        smtpHost: mailboxForm.smtpHost,
        smtpPort: parseInt(mailboxForm.smtpPort) || 465,
      } : null

      if (editingMailbox) {
        await api.patch(`/mailboxes/${editingMailbox.id}`, {
          name: mailboxForm.name,
          email: mailboxForm.email || undefined,
          department: mailboxForm.department || undefined,
          provider: mailboxForm.provider,
          providerConfig,
        })
        successToast('Mailbox actualizado')
      } else {
        await api.post('/mailboxes', {
          address: mailboxForm.address,
          email: mailboxForm.email || undefined,
          name: mailboxForm.name,
          department: mailboxForm.department || undefined,
          provider: mailboxForm.provider,
          providerConfig,
        })
        successToast('Mailbox creado exitosamente')
      }
      setShowMailboxModal(false)
      setLastError(null)
      loadMailboxes()
    } catch (err: any) {
      const msg = err.message || 'Error al guardar mailbox'
      setLastError(msg)
      errorToast(msg)
    }
  }

  if (isLoading && activeSection === 'company') {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Configuración</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Administra la configuración del sistema</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <Card className="lg:w-64 flex-shrink-0">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  activeSection === section.id
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                }`}
              >
                <section.icon className="w-5 h-5" />
                {section.name}
              </button>
            ))}
          </nav>
        </Card>

        <div className="flex-1 space-y-6">
          {activeSection === 'profile' && (
             <Card header={<h3 className="font-semibold text-gray-900 dark:text-gray-100">Información del Perfil</h3>}>
              <div className="space-y-6">
                {/* Avatar upload */}
                <div className="flex items-center gap-6">
                  <div className="flex-shrink-0">
                    {profileForm.avatarUrl ? (
                      <div className="relative">
                        <img
                          src={getAssetUrl(profileForm.avatarUrl)}
                          alt="Foto de perfil"
                          className="w-24 h-24 object-cover rounded-full border-2 border-gray-200 dark:border-gray-700"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = ''
                            setProfileForm(prev => ({ ...prev, avatarUrl: '' }))
                          }}
                        />
                        <label className="absolute -bottom-1 -right-1 p-1.5 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <Upload className="w-4 h-4 text-gray-500" />
                          <input type="file" accept="image/*" className="sr-only" onChange={handleAvatarUpload} disabled={uploadingAvatar} />
                        </label>
                      </div>
                    ) : (
                      <label className={cn(
                        'flex flex-col items-center justify-center w-24 h-24 rounded-full border-2 border-dashed cursor-pointer transition-colors',
                        'border-gray-300 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                      )}>
                        {uploadingAvatar ? (
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        ) : (
                          <>
                            <User className="w-8 h-8 text-gray-400" />
                          </>
                        )}
                        <input type="file" accept="image/*" className="sr-only" onChange={handleAvatarUpload} disabled={uploadingAvatar} />
                      </label>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Foto de perfil</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">PNG, JPG o GIF, máximo 2MB.</p>
                    {profileForm.avatarUrl && (
                      <button
                        onClick={() => setProfileForm(prev => ({ ...prev, avatarUrl: '' }))}
                        className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Quitar foto
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Nombres"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                  <Input
                    label="Apellidos"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
                <Input label="Correo electrónico" type="email" defaultValue={user?.email || ''} disabled />
                <div className="flex justify-end">
                  <Button>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar cambios
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {activeSection === 'notifications' && (
             <Card header={<h3 className="font-semibold text-gray-900 dark:text-gray-100">Preferencias de Notificaciones</h3>}>
              <div className="space-y-4">
                {[
                  { label: 'Alertas críticas', description: 'Recibir notificaciones de alertas críticas inmediatamente' },
                  { label: 'Nuevos casos asignados', description: 'Notificar cuando se asigne un nuevo caso' },
                  { label: 'Vencimiento de SLA', description: 'Alertar cuando se acerque la fecha límite de SLA' },
                  { label: 'Resumen diario', description: 'Recibir un resumen diario de actividad' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-9 h-5 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600" />
                    </label>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <Button>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar preferencias
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {activeSection === 'appearance' && <AppearanceSection />}

          {activeSection === 'security' && (
            <>
               <Card header={<h3 className="font-semibold text-gray-900 dark:text-gray-100">Verificación en 2 pasos (2FA)</h3>}>
                <TwoFactorSection />
              </Card>

               <Card header={<h3 className="font-semibold text-gray-900 dark:text-gray-100">Cambiar contraseña</h3>}>
                <div className="space-y-4">
                  <Input
                    label="Contraseña actual"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    disabled={isChangingPassword}
                  />
                  <Input
                    label="Nueva contraseña"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    disabled={isChangingPassword}
                  />
                  <Input
                    label="Confirmar nueva contraseña"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    disabled={isChangingPassword}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleChangePassword} disabled={isChangingPassword}>
                      <Save className="w-4 h-4 mr-2" />
                      {isChangingPassword ? 'Actualizando...' : 'Actualizar contraseña'}
                    </Button>
                  </div>
                </div>
              </Card>
            </>
          )}

          {activeSection === 'company' && (
            <Card>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 rounded-t-xl">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                   <h3 className="font-semibold text-gray-900 dark:text-gray-100">Información de la Empresa</h3>
                </div>
                {isAdmin ? (
                  <span className="text-xs font-medium text-primary-600 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded-full border border-primary-200 dark:border-primary-800/50">
                    Puedes editar (ADMIN)
                  </span>
                ) : (
                  <span className="text-xs font-medium text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded-full border border-orange-200 dark:border-orange-800/50">
                    Solo lectura ({userRole})
                  </span>
                )}
              </div>

              <div className="p-6 space-y-6">
                {/* Error message */}
                {lastError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <Shield className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Error</p>
                      <p className="text-xs text-red-600">{lastError}</p>
                    </div>
                  </div>
                )}

                {/* Logo upload */}
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    {companyForm.logoUrl ? (
                      <div className="relative">
                        <img
                          src={getAssetUrl(companyForm.logoUrl)}
                          alt="Logo empresa"
                          className="w-24 h-24 object-contain rounded-xl border border-gray-200 bg-white"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                        {isAdmin && (
                          <label className="absolute -bottom-2 -right-2 p-1.5 bg-white rounded-full border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors">
                            <Upload className="w-4 h-4 text-gray-500" />
                            <input type="file" accept="image/*" className="sr-only" onChange={handleLogoUpload} disabled={uploadingLogo} />
                          </label>
                        )}
                      </div>
                    ) : (
                      <label className={cn(
                        'flex flex-col items-center justify-center w-24 h-24 rounded-xl border-2 border-dashed cursor-pointer transition-colors',
                        isAdmin ? 'border-gray-300 hover:border-primary-400 hover:bg-gray-50' : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      )}>
                        {uploadingLogo ? (
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        ) : (
                          <>
                            <ImagePlus className="w-6 h-6 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-400">Subir logo</span>
                          </>
                        )}
                        <input type="file" accept="image/*" className="sr-only" onChange={handleLogoUpload} disabled={uploadingLogo || !isAdmin} />
                      </label>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Logo de la empresa</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">PNG o JPG, máximo 2MB. Se mostrará en el sidebar.</p>
                    {companyForm.logoUrl && isAdmin && (
                      <button
                        onClick={() => setCompanyForm(prev => ({ ...prev, logoUrl: '' }))}
                        className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Quitar logo
                      </button>
                    )}
                    {!isAdmin && <p className="text-xs text-gray-400">Solo el administrador puede cambiar el logo.</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre de la empresa</label>
                    <input
                      type="text"
                      value={companyForm.name}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, name: e.target.value }))}
                      disabled={!isAdmin}
                      className={cn(
                        'block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                        !isAdmin && 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      )}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">NIT</label>
                    <input
                      type="text"
                      value={companyForm.nit}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, nit: e.target.value }))}
                      disabled={!isAdmin}
                      className={cn(
                        'block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                        !isAdmin && 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Razón social</label>
                  <input
                    type="text"
                    value={companyForm.businessName || ''}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, businessName: e.target.value }))}
                    disabled={!isAdmin}
                    className={cn(
                      'block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                      !isAdmin && 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
                    <input
                      type="text"
                      value={companyForm.phone || ''}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, phone: e.target.value }))}
                      disabled={!isAdmin}
                      className={cn(
                        'block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                        !isAdmin && 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      )}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Correo corporativo</label>
                    <input
                      type="email"
                      value={companyForm.mainEmail || ''}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, mainEmail: e.target.value }))}
                      disabled={!isAdmin}
                      className={cn(
                        'block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                        !isAdmin && 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contacto principal</label>
                  <input
                    type="text"
                    value={companyForm.mainContact || ''}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, mainContact: e.target.value }))}
                    disabled={!isAdmin}
                    className={cn(
                      'block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                      !isAdmin && 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    )}
                  />
                </div>

                {isAdmin && (
                  <div className="flex justify-end pt-2">
                    <Button onClick={handleSaveCompany}>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar cambios
                    </Button>
                  </div>
                )}
                {!isAdmin && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">Solo el administrador puede modificar los datos de la empresa.</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {activeSection === 'email' && (
             <Card header={<h3 className="font-semibold text-gray-900 dark:text-gray-100">Configuración de Correo</h3>}>
              <div className="space-y-4">
                <Input label="Buzón principal" defaultValue="rrhh@humantalentsas.com" />
                <Input label="Buzón legal" defaultValue="legal@humantalentsas.com" />
                <Input label="Buzón financiero" defaultValue="financiera@humantalentsas.com" />
                <div className="flex justify-end">
                  <Button>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar configuración
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {activeSection === 'users' && isAdmin && (
            <Card header={
              <div className="flex items-center justify-between">
                 <h3 className="font-semibold text-gray-900 dark:text-gray-100">Usuarios de la Empresa</h3>
                <Button onClick={() => handleOpenUserModal()} size="sm">
                  <User className="w-4 h-4 mr-1" />
                  Nuevo Usuario
                </Button>
              </div>
            }>
              {isLoadingUsers ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Usuario</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rol</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Área</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {companyUsers?.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">{u.firstName[0]}{u.lastName[0]}</span>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.firstName} {u.lastName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              u.role === 'ADMIN' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                              u.role === 'MANAGER' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' :
                              'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {u.area?.name || 'Sin área'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              u.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {u.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleOpenUserModal(u)}
                                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleOpenResetPassword(u)}
                                className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
                                title="Restablecer contraseña"
                              >
                                Contraseña
                              </button>
                              <button
                                onClick={() => handleToggleStatus(u.id)}
                                className={`${u.isActive ? 'text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300' : 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'}`}
                                title={u.isActive ? 'Desactivar' : 'Activar'}
                              >
                                {u.isActive ? 'Desactivar' : 'Activar'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!companyUsers || companyUsers.length === 0) && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                      No hay usuarios registrados
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {activeSection === 'mailboxes' && isAdmin && (
            <Card header={
              <div className="flex items-center justify-between">
                 <h3 className="font-semibold text-gray-900 dark:text-gray-100">Mailboxes / Buzones de Correo</h3>
                <Button onClick={() => handleOpenMailboxModal()} size="sm">
                  <Mail className="w-4 h-4 mr-1" />
                  Nuevo Mailbox
                </Button>
              </div>
            }>
              {isLoadingMailboxes ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nombre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dirección</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Departamento</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Proveedor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {mailboxes.map((mb) => (
                        <tr key={mb.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{mb.name}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{mb.address}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{mb.department || '-'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              mb.provider === 'GMAIL' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                              mb.provider === 'ZOHO' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {mb.provider}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              mb.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {mb.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleOpenMailboxModal(mb)}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                              title="Editar"
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {mailboxes.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                      No hay mailboxes configurados
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Modal para crear/editar mailbox */}
          {showMailboxModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {editingMailbox ? 'Editar Mailbox' : 'Nuevo Mailbox'}
                    </h3>
                    <button
                      onClick={() => setShowMailboxModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {lastError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {lastError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dirección (email principal)</label>
                      <input
                        type="text"
                        value={mailboxForm.address}
                        onChange={(e) => setMailboxForm(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="ej: contacto@empresa.com"
                        disabled={!!editingMailbox}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 dark:disabled:bg-gray-800"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email para envío (opcional)</label>
                      <input
                        type="text"
                        value={mailboxForm.email}
                        onChange={(e) => setMailboxForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="ej: contacto@empresa.com"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre del mailbox</label>
                      <input
                        type="text"
                        value={mailboxForm.name}
                        onChange={(e) => setMailboxForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="ej: Soporte al Cliente"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Departamento</label>
                      <input
                        type="text"
                        value={mailboxForm.department}
                        onChange={(e) => setMailboxForm(prev => ({ ...prev, department: e.target.value }))}
                        placeholder="ej: Servicio al Cliente"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Proveedor</label>
                      <select
                        value={mailboxForm.provider}
                        onChange={(e) => setMailboxForm(prev => ({ ...prev, provider: e.target.value }))}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="GMAIL">Gmail / Google Workspace</option>
                        <option value="ZOHO">Zoho Mail</option>
                        <option value="OUTLOOK">Outlook / Microsoft 365</option>
                        <option value="OTHER">Otro (IMAP genérico)</option>
                      </select>
                    </div>

                    {mailboxForm.provider === 'ZOHO' && (
                      <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Configuración Zoho Mail (requiere App Password)</p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">App Password</label>
                          <input
                            type="password"
                            value={mailboxForm.appPassword}
                            onChange={(e) => setMailboxForm(prev => ({ ...prev, appPassword: e.target.value }))}
                            placeholder="Generada en Zoho Mail → Seguridad → App Passwords"
                            className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IMAP Host</label>
                            <input
                              type="text"
                              value={mailboxForm.imapHost}
                              onChange={(e) => setMailboxForm(prev => ({ ...prev, imapHost: e.target.value }))}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IMAP Port</label>
                            <input
                              type="text"
                              value={mailboxForm.imapPort}
                              onChange={(e) => setMailboxForm(prev => ({ ...prev, imapPort: e.target.value }))}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Host</label>
                            <input
                              type="text"
                              value={mailboxForm.smtpHost}
                              onChange={(e) => setMailboxForm(prev => ({ ...prev, smtpHost: e.target.value }))}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Port</label>
                            <input
                              type="text"
                              value={mailboxForm.smtpPort}
                              onChange={(e) => setMailboxForm(prev => ({ ...prev, smtpPort: e.target.value }))}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <Button variant="secondary" onClick={() => setShowMailboxModal(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveMailbox}>
                      <Save className="w-4 h-4 mr-2" />
                      {editingMailbox ? 'Guardar Cambios' : 'Crear Mailbox'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal para crear/editar usuario */}
          {showUserModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                    </h3>
                    <button
                      onClick={() => setShowUserModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {lastError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {lastError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
                        <input
                          type="text"
                          value={userForm.firstName}
                          onChange={(e) => setUserForm(prev => ({ ...prev, firstName: e.target.value }))}
                          className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido</label>
                        <input
                          type="text"
                          value={userForm.lastName}
                          onChange={(e) => setUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                          className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Correo electrónico</label>
                      <input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                        disabled={editingUser !== null}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400"
                      />
                    </div>

                    {!editingUser && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contraseña</label>
                        <input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Mínimo 8 caracteres"
                          className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol</label>
                      <select
                        value={userForm.role}
                        onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value }))}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="ADMIN">Administrador</option>
                        <option value="MANAGER">Gerente</option>
                        <option value="OPERATOR">Operador</option>
                        <option value="LEGAL">Legal</option>
                        <option value="HR">RRHH</option>
                        <option value="FINANCE">Finanzas</option>
                        <option value="VIEWER">Visualizador</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Área (opcional)</label>
                      <select
                        value={userForm.areaId}
                        onChange={(e) => setUserForm(prev => ({ ...prev, areaId: e.target.value }))}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Sin área</option>
                        {areas.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <Button variant="secondary" onClick={() => setShowUserModal(false)}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveUser}
                      disabled={createUser.isPending || updateUser.isPending}
                    >
                      {(createUser.isPending || updateUser.isPending) ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MODAL: Restablecer contraseña */}
          {resetPasswordUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={handleCloseResetPassword}>
              <div className="w-full max-w-md card p-6" onClick={e => e.stopPropagation()}>
                {!resetResult ? (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Restablecer contraseña</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{resetPasswordUser.firstName} {resetPasswordUser.lastName} · {resetPasswordUser.email}</p>
                      </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <p className="text-xs text-amber-800">
                        Se generará una nueva contraseña temporal. El usuario deberá cambiarla en su próximo inicio de sesión.
                      </p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={handleCloseResetPassword}>
                        Cancelar
                      </Button>
                      <Button onClick={handleConfirmResetPassword} isLoading={adminResetPassword.isPending}>
                        Restablecer
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Contraseña restablecida</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{resetPasswordUser.firstName} {resetPasswordUser.lastName}</p>
                      </div>
                    </div>
                    {resetResult.emailSent ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                        <p className="text-xs text-emerald-800">
                          Se envió un email a <strong>{resetPasswordUser.email}</strong> con la nueva contraseña temporal.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                          <p className="text-xs text-amber-800">
                            {resetResult.devTempPassword
                              ? 'Mailer deshabilitado en desarrollo. Comparte esta contraseña con el usuario:'
                              : 'No se pudo enviar el email. Comparte esta contraseña con el usuario:'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                          <code className="flex-1 font-mono text-sm select-all break-all">
                            {resetResult.devTempPassword || resetResult.tempPassword || '—'}
                          </code>
                          <button
                            onClick={() => handleCopyTempPassword(resetResult.devTempPassword || resetResult.tempPassword || '')}
                            className="p-1.5 text-gray-400 hover:text-primary-600 flex-shrink-0"
                            title="Copiar"
                          >
                            {resetCopied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </>
                    )}
                    {resetResult.mustChangePassword && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        El usuario deberá cambiar esta contraseña en su próximo inicio de sesión.
                      </p>
                    )}
                    <div className="flex justify-end">
                      <Button onClick={handleCloseResetPassword}>
                        Listo
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeSection === 'reclassify' && isAdmin && (
            <ReclassifySection api={api} successToast={successToast} errorToast={errorToast} />
          )}

          {activeSection === 'attachments' && isAdmin && (
            <AttachmentsSection api={api} successToast={successToast} errorToast={errorToast} />
          )}

          {activeSection === 'activity' && isAdmin && (
            <AccountActivitySection />
          )}

          {activeSection === 'data-cleanup' && isAdmin && (
            <DataCleanupSection api={api} successToast={successToast} errorToast={errorToast} />
          )}
        </div>
      </div>
    </div>
  )
}

function AppearanceSection() {
  const { theme, setTheme } = useTheme()
  const options: Array<{ id: 'light' | 'dark' | 'system'; label: string; description: string; icon: any }> = [
    { id: 'light', label: 'Claro', description: 'Fondo blanco, texto oscuro', icon: Sun },
    { id: 'dark', label: 'Oscuro', description: 'Fondo oscuro, texto claro', icon: Moon },
    { id: 'system', label: 'Sistema', description: 'Sigue la configuración de tu dispositivo', icon: Monitor },
  ]
  return (
    <Card header={<h3 className="font-semibold text-gray-900 dark:text-gray-100">Tema</h3>}>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Elige cómo se ve la aplicación. Los cambios se aplican de inmediato.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {options.map((opt) => {
          const Icon = opt.icon
          const active = theme === opt.id
          return (
            <button
              key={opt.id}
              onClick={() => setTheme(opt.id)}
              className={cn(
                'flex flex-col items-start gap-2 p-4 rounded-lg border-2 text-left transition-all',
                active
                  ? 'border-primary-500 bg-primary-50/40 dark:bg-primary-900/20 ring-1 ring-primary-500'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50'
              )}
              aria-pressed={active}
            >
              <div className="flex items-center gap-2">
                <Icon className={cn('w-5 h-5', active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400')} />
                <span className={cn('text-sm font-medium', active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100')}>
                  {opt.label}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{opt.description}</p>
            </button>
          )
        })}
      </div>
    </Card>
  )
}

interface ReclassifyJob {
  id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  totalEmails: number
  processed: number
  succeeded: number
  failed: number
  skipped: number
  months: number
  currentEmailId?: string | null
  lastError?: string | null
  startedAt?: string | null
  completedAt?: string | null
  createdAt: string
}

interface ReclassifySectionProps {
  api: any
  successToast: (msg: string) => void
  errorToast: (msg: string) => void
}

function ReclassifySection({ api, successToast, errorToast }: ReclassifySectionProps) {
  const [months, setMonths] = useState(6)
  const [includeExisting, setIncludeExisting] = useState(true)
  const [includeNew, setIncludeNew] = useState(false)
  const [limit, setLimit] = useState<number | ''>('')
  const [batchSize, setBatchSize] = useState(5)
  const [concurrency, setConcurrency] = useState(3)
  const [isStarting, setIsStarting] = useState(false)
  const [activeJob, setActiveJob] = useState<ReclassifyJob | null>(null)
  const [confirmCleanup, setConfirmCleanup] = useState(false)
  const [isCleaning, setIsCleaning] = useState(false)
  const [isMciCleaning, setIsMciCleaning] = useState(false)
  const [mciCleanupStatus, setMciCleanupStatus] = useState<{ cleaned?: number; synced?: number; errors?: number; message?: string } | null>(null)

  const pollJob = useCallback(async (jobId: string) => {
    try {
      const res = await api.get(`/reclassify/${jobId}`)
      setActiveJob(res)
      if (res.status === 'running' || res.status === 'pending') {
        setTimeout(() => pollJob(jobId), 2000)
      } else {
        if (res.status === 'completed') successToast(`Re-clasificación completada: ${res.succeeded} ok, ${res.failed} fallidos`)
        else if (res.status === 'failed') errorToast(`Job falló: ${res.lastError || 'error desconocido'}`)
      }
    } catch (err: any) {
      errorToast(`Error consultando job: ${err.message}`)
    }
  }, [api, successToast, errorToast])

  const startReclassify = async () => {
    setIsStarting(true)
    try {
      const body: any = {
        months,
        includeExisting,
        includeNew,
        batchSize,
        concurrency,
      }
      if (limit) body.limit = Number(limit)
      const res = await api.post('/reclassify', body)
      if (res.success) {
        successToast(`Job creado: ${res.jobId}`)
        pollJob(res.jobId)
      } else {
        errorToast('No se pudo crear el job')
      }
    } catch (err: any) {
      errorToast(`Error: ${err.message || err}`)
    } finally {
      setIsStarting(false)
    }
  }

  const cancelJob = async () => {
    if (!activeJob) return
    try {
      await api.post(`/reclassify/${activeJob.id}/cancel`)
      successToast('Cancelación solicitada')
    } catch (err: any) {
      errorToast(`Error: ${err.message}`)
    }
  }

  const cleanupClassification = async () => {
    setIsCleaning(true)
    try {
      const res = await api.post('/admin/cleanup-classification')
      successToast(`Limpieza OK: ${res.deleted ? JSON.stringify(res.deleted) : 'hecho'}`)
      setConfirmCleanup(false)
    } catch (err: any) {
      errorToast(`Error: ${err.message}`)
    } finally {
      setIsCleaning(false)
    }
  }

  const sync6Months = async () => {
    try {
      const res = await api.post('/email-sync/months', { months })
      successToast(`Sync ${months}m: ${res.totalSynced} nuevos, ${res.processed} a procesar`)
    } catch (err: any) {
      errorToast(`Error: ${err.message}`)
    }
  }

  const startMciCleanup = async () => {
    setIsMciCleaning(true)
    try {
      const res = await api.post('/reclassify/cleanup-mci')
      if (res.success) {
        successToast('Cleanup MCI iniciado en background')
        pollMciCleanupStatus()
      }
    } catch (err: any) {
      errorToast(`Error: ${err.message}`)
      setIsMciCleaning(false)
    }
  }

  const pollMciCleanupStatus = useCallback(async () => {
    try {
      const res = await api.get('/reclassify/cleanup-mci/status')
      setMciCleanupStatus(res)
      if (res.message?.includes('completado') || res.cleaned !== undefined) {
        setIsMciCleaning(false)
      } else {
        setTimeout(pollMciCleanupStatus, 3000)
      }
    } catch {
      setIsMciCleaning(false)
    }
  }, [api])

  const progressPct = activeJob && activeJob.totalEmails > 0
    ? Math.round((activeJob.processed / activeJob.totalEmails) * 100)
    : 0

  return (
    <div className="space-y-6">
      {/* Card: Re-clasificación */}
      <Card
        header={
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Re-clasificación masiva con IA</h3>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Re-clasifica los correos existentes con ClassificationEngine v2 usando OpenAI.
          La IA interpreta el contenido y aplica la nueva taxonomía de etiquetas:
          CLASIFICACION/, CRITICIDAD/, ACCION/, ENTIDAD/, AREA/, IA/, CASO/, ALERTA/.
          Un criteria engine valida el resultado para evitar falsos positivos.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Período (meses hacia atrás)
            </label>
            <input
              type="number"
              min={1}
              max={24}
              value={months}
              onChange={(e) => setMonths(Math.max(1, Math.min(24, Number(e.target.value) || 6)))}
              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Límite (opcional, dejar vacío = todos)
            </label>
            <input
              type="number"
              min={1}
              value={limit}
              onChange={(e) => setLimit(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Sin límite"
              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Batch size
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={batchSize}
              onChange={(e) => setBatchSize(Math.max(1, Math.min(50, Number(e.target.value) || 5)))}
              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Concurrencia
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={concurrency}
              onChange={(e) => setConcurrency(Math.max(1, Math.min(10, Number(e.target.value) || 3)))}
              className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeExisting}
              onChange={(e) => setIncludeExisting(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <Database className="w-4 h-4" />
            Re-clasificar emails ya en BD
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeNew}
              onChange={(e) => setIncludeNew(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <Zap className="w-4 h-4" />
            Traer nuevos de los últimos {months} meses desde Gmail/Zoho
          </label>
        </div>

        {activeJob && (
          <div className="mb-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {activeJob.status === 'running' || activeJob.status === 'pending' ? (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                ) : activeJob.status === 'completed' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : activeJob.status === 'cancelled' ? (
                  <Pause className="w-4 h-4 text-amber-600" />
                ) : (
                  <X className="w-4 h-4 text-red-600" />
                )}
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Job: <code className="text-xs">{activeJob.id.slice(0, 8)}</code> — {activeJob.status}
                </span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {activeJob.processed} / {activeJob.totalEmails} ({progressPct}%)
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-primary-600 transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-green-700 dark:text-green-400">✓ {activeJob.succeeded} ok</div>
              <div className="text-red-700 dark:text-red-400">✗ {activeJob.failed} fallidos</div>
              <div className="text-gray-500 dark:text-gray-400">⤴ {activeJob.skipped} skip</div>
            </div>
            {activeJob.lastError && (
              <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-300">
                {activeJob.lastError}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {(activeJob?.status === 'running' || activeJob?.status === 'pending') ? (
            <Button onClick={cancelJob} variant="secondary">
              <Pause className="w-4 h-4 mr-2" />
              Cancelar job
            </Button>
          ) : (
            <Button onClick={startReclassify} disabled={isStarting}>
              {isStarting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Iniciar re-clasificación
            </Button>
          )}
        </div>
      </Card>

      {/* Card: Sincronización de últimos N meses */}
      <Card
        header={
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Sincronizar últimos {months} meses</h3>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Trae correos nuevos de Gmail (después de {months} meses) o Zoho (sin filtro de fecha exacto).
          Los correos nuevos se procesan automáticamente con IA.
        </p>
        <Button onClick={sync6Months} variant="secondary">
          <Mail className="w-4 h-4 mr-2" />
          Traer correos de los últimos {months} meses
        </Button>
      </Card>

      {/* Card: Cleanup MCI legacy */}
      <Card
        header={
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Limpiar labels MCI legacy</h3>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Elimina todos los labels MCI v4 legacy de los emails en BD y en Gmail.
          Estos labels ya no se usan con el nuevo ClassificationEngine v2.
        </p>
        {mciCleanupStatus && !isMciCleaning && (
          <div className="mb-3 p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-xs">
            <span className="text-amber-700 dark:text-amber-300">
              Labels MCI limpiados: {mciCleanupStatus.cleaned} | Sincronizados con Gmail: {mciCleanupStatus.synced} | Errores: {mciCleanupStatus.errors}
            </span>
          </div>
        )}
        <Button onClick={startMciCleanup} disabled={isMciCleaning} variant="secondary">
          {isMciCleaning ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4 mr-2" />
          )}
          {isMciCleaning ? 'Limpiando...' : 'Limpiar labels MCI legacy'}
        </Button>
      </Card>

      {/* Card: Limpieza (peligrosa) */}
      <Card
        header={
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Limpieza de clasificación</h3>
            <span className="text-xs px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
              ⚠️ Peligroso
            </span>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>Trunca</strong>: Case, Alert, Escalation, AIAnalysis, IAResponse, CaseCommunication.<br />
          <strong>Resetea</strong>: Email.labels, Email.caseId.<br />
          <strong>Conserva</strong>: Emails, Mailboxes, Users, Areas, Reglas MCI.
        </p>
        <p className="text-xs text-amber-700 dark:text-amber-400 mb-4">
          Haz un dump de la BD local antes de ejecutar. Esta acción no se puede deshacer.
        </p>
        {!confirmCleanup ? (
          <Button onClick={() => setConfirmCleanup(true)} variant="secondary" className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Limpiar datos de clasificación
          </Button>
        ) : (
          <div className="flex gap-3">
            <Button onClick={cleanupClassification} disabled={isCleaning} variant="secondary" className="text-red-600">
              {isCleaning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Confirmar limpieza
            </Button>
            <Button onClick={() => setConfirmCleanup(false)} variant="secondary">
              Cancelar
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}

interface AttachmentsStats {
  total: number
  totalSizeBytes: number
  totalSizeMB: number
  infected: number
  breakdown: {
    lastMonth: number
    last3Months: number
    last6Months: number
    olderThan6Months: number
  }
  expiringSoon: number
  pendingCleanup: number
  retentionDays: number
  enabled: boolean
}

interface QuarantineItem {
  id: string
  path: string
  originalName: string | null
  virus: string
  quarantinedAt: string
  size: number
}

interface ScannerStatus {
  scannerAvailable: boolean
  scannerVersion: string | null
  enabled: boolean
  required: boolean
  totalScans: number
  clean: number
  infected: number
  errors: number
  skipped: number
}

interface CleanupLog {
  id: string
  startedAt: string
  completedAt: string | null
  cutoffDate: string
  mode: string
  dryRun: boolean
  totalCandidates: number
  deletedFiles: number
  freedBytes: string
  errors: number
  errorDetails: string | null
  triggeredBy: string | null
}

interface AttachmentsSectionProps {
  api: any
  successToast: (msg: string) => void
  errorToast: (msg: string) => void
}

function AttachmentsSection({ api, successToast, errorToast }: AttachmentsSectionProps) {
  const [stats, setStats] = useState<AttachmentsStats | null>(null)
  const [scanner, setScanner] = useState<ScannerStatus | null>(null)
  const [quarantine, setQuarantine] = useState<QuarantineItem[]>([])
  const [history, setHistory] = useState<CleanupLog[]>([])
  const [loading, setLoading] = useState(false)
  const [cleanupRunning, setCleanupRunning] = useState(false)
  const [confirmCleanup, setConfirmCleanup] = useState(false)
  const [dryRun, setDryRun] = useState(true)

  const loadAll = async () => {
    setLoading(true)
    try {
      const [s, sc, q, h] = await Promise.all([
        api.get('/admin/attachments/stats'),
        api.get('/admin/attachments/scanner/status'),
        api.get('/admin/attachments/quarantine'),
        api.get('/admin/attachments/cleanup/history'),
      ])
      setStats(s)
      setScanner(sc)
      setQuarantine(q || [])
      setHistory(h || [])
    } catch (err: any) {
      errorToast(`Error cargando datos: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
  }, [])

  const runCleanup = async (preview: boolean) => {
    setCleanupRunning(true)
    try {
      const res = await api.post('/admin/attachments/cleanup', { dryRun: preview })
      if (preview) {
        successToast(`Preview: ${res.totalCandidates} adjuntos serían eliminados (${(res.freedBytes / 1024 / 1024).toFixed(2)}MB)`)
      } else {
        successToast(`Limpieza OK: ${res.deletedFiles} borrados, ${(res.freedBytes / 1024 / 1024).toFixed(2)}MB liberados`)
      }
      setConfirmCleanup(false)
      await loadAll()
    } catch (err: any) {
      errorToast(`Error: ${err.message}`)
    } finally {
      setCleanupRunning(false)
    }
  }

  const testScanner = async () => {
    try {
      const res = await api.post('/admin/attachments/scanner/test')
      if (res.ok) {
        successToast(`Scanner OK: ${res.version}`)
      } else {
        errorToast(`Scanner no disponible: ${res.error}`)
      }
    } catch (err: any) {
      errorToast(`Error: ${err.message}`)
    }
  }

  const formatBytes = (b: number) => {
    if (b < 1024) return `${b} B`
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(2)} MB`
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`
  }

  return (
    <div className="space-y-6">
      {/* Card: Scanner antivirus */}
      <Card
        header={
          <div className="flex items-center gap-2">
            <FileLock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Antivirus (ClamAV)</h3>
          </div>
        }
      >
        {scanner && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {scanner.scannerAvailable ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                  <ShieldCheck className="w-3 h-3" />
                  Activo: {scanner.scannerVersion}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  <ShieldAlert className="w-3 h-3" />
                  No disponible (modo permisivo)
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Total scans</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{scanner.totalScans}</div>
              </div>
              <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Limpios</div>
                <div className="text-lg font-semibold text-green-700 dark:text-green-300">{scanner.clean}</div>
              </div>
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Infectados</div>
                <div className="text-lg font-semibold text-red-700 dark:text-red-300">{scanner.infected}</div>
              </div>
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Skipped</div>
                <div className="text-lg font-semibold text-amber-700 dark:text-amber-300">{scanner.skipped}</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Errores</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{scanner.errors}</div>
              </div>
            </div>
            <Button onClick={testScanner} variant="secondary" size="sm">
              <Zap className="w-4 h-4 mr-2" />
              Probar scanner
            </Button>
          </div>
        )}
        {quarantine.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-2 flex items-center gap-1">
              <Bug className="w-4 h-4" />
              Archivos en cuarentena ({quarantine.length})
            </h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {quarantine.slice(0, 10).map((q) => (
                <div key={q.id} className="text-xs flex items-center justify-between p-2 rounded bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono truncate text-gray-900 dark:text-gray-100">{q.originalName || '(sin nombre)'}</div>
                    <div className="text-red-600 dark:text-red-400">{q.virus}</div>
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 ml-2">{formatBytes(q.size)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Card: Stats */}
      {stats && (
        <Card
          header={
            <div className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Almacenamiento de adjuntos</h3>
            </div>
          }
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Total adjuntos</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stats.total}</div>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Espacio usado</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stats.totalSizeMB} MB</div>
            </div>
            <div className={`rounded-lg p-3 ${stats.infected > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
              <div className="text-xs text-gray-500 dark:text-gray-400">Infectados</div>
              <div className={`text-lg font-semibold ${stats.infected > 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}>
                {stats.infected}
              </div>
            </div>
            <div className={`rounded-lg p-3 ${stats.pendingCleanup > 0 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
              <div className="text-xs text-gray-500 dark:text-gray-400">Pendientes limpieza</div>
              <div className={`text-lg font-semibold ${stats.pendingCleanup > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-gray-100'}`}>
                {stats.pendingCleanup}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="flex justify-between p-2 rounded bg-gray-50 dark:bg-gray-800/50">
              <span className="text-gray-500 dark:text-gray-400">Último mes:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{stats.breakdown.lastMonth}</span>
            </div>
            <div className="flex justify-between p-2 rounded bg-gray-50 dark:bg-gray-800/50">
              <span className="text-gray-500 dark:text-gray-400">1-3 meses:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{stats.breakdown.last3Months}</span>
            </div>
            <div className="flex justify-between p-2 rounded bg-gray-50 dark:bg-gray-800/50">
              <span className="text-gray-500 dark:text-gray-400">3-6 meses:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{stats.breakdown.last6Months}</span>
            </div>
            <div className="flex justify-between p-2 rounded bg-gray-50 dark:bg-gray-800/50">
              <span className="text-gray-500 dark:text-gray-400">+6 meses:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{stats.breakdown.olderThan6Months}</span>
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            Retención: <strong>{stats.retentionDays} días</strong> ({Math.round(stats.retentionDays / 30)} meses).
            Auto-limpieza: {stats.enabled ? '✅ Activa (cron diario 03:00)' : '❌ Deshabilitada'}.
            Próximos a expirar (7 días): {stats.expiringSoon}.
          </p>
        </Card>
      )}

      {/* Card: Limpieza manual */}
      <Card
        header={
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Limpieza manual</h3>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Ejecuta la limpieza de adjuntos manualmente. La limpieza automática corre todos los días a las 03:00.
        </p>
        <div className="flex items-center gap-2 mb-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Solo preview (no borrar)
          </label>
        </div>
        {!confirmCleanup ? (
          <div className="flex gap-3">
            <Button onClick={() => runCleanup(true)} variant="secondary" disabled={cleanupRunning}>
              {cleanupRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Preview
            </Button>
            <Button onClick={() => setConfirmCleanup(true)} variant="secondary" className="text-amber-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Ejecutar limpieza real
            </Button>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button onClick={() => runCleanup(false)} disabled={cleanupRunning} className="text-red-600">
              {cleanupRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Confirmar borrado
            </Button>
            <Button onClick={() => setConfirmCleanup(false)} variant="secondary">
              Cancelar
            </Button>
          </div>
        )}
      </Card>

      {/* Card: Historial de limpiezas */}
      {history.length > 0 && (
        <Card header={<h3 className="font-semibold text-gray-900 dark:text-gray-100">Historial de limpiezas</h3>}>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="text-sm p-3 rounded border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      h.mode === 'scheduled' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                      h.mode === 'manual' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {h.mode}
                    </span>
                    {h.dryRun && <span className="px-2 py-0.5 text-xs rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">DRY</span>}
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(h.startedAt).toLocaleString('es-CO')}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <div>Candidatos: <span className="font-semibold text-gray-900 dark:text-gray-100">{h.totalCandidates}</span></div>
                  <div>Borrados: <span className="font-semibold text-gray-900 dark:text-gray-100">{h.deletedFiles}</span></div>
                  <div>Liberado: <span className="font-semibold text-gray-900 dark:text-gray-100">{formatBytes(Number(h.freedBytes))}</span></div>
                </div>
                {h.errors > 0 && (
                  <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {h.errors} errores
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
        </div>
      )}

      <Button onClick={loadAll} variant="secondary" size="sm">
        <RefreshCw className="w-4 h-4 mr-2" />
        Refrescar
      </Button>
    </div>
  )
}

interface DataCleanupSectionProps {
  api: any
  successToast: (msg: string) => void
  errorToast: (msg: string) => void
}

function DataCleanupSection({ api, successToast, errorToast }: DataCleanupSectionProps) {
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<any>(null)

  const handleCleanup = async () => {
    setLoading(true)
    try {
      const res = await api.post('/admin/cleanup-all-data')
      setResult(res)
      setConfirming(false)
      successToast('Datos eliminados correctamente')
    } catch (err: any) {
      errorToast(`Error: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card
        header={
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Limpiar Datos Operativos</h3>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-200"> Acción irreversible</h4>
                <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                  Esta acción eliminará permanentemente todos los emails, casos y alertas. Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">Se eliminarán:</h5>
            <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <li>• <strong>Emails</strong> - Todos los emails sincronizados</li>
              <li>• <strong>Casos</strong> - Todos los casos creados</li>
              <li>• <strong>Alertas</strong> - Todas las alertas generadas</li>
              <li>• AI Analysis, IAResponse, Communications, States</li>
            </ul>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <h5 className="text-xs font-semibold text-green-800 dark:text-green-200 mb-2">Se preservarán:</h5>
            <ul className="text-xs text-green-700 dark:text-green-300 space-y-1">
              <li>• Usuarios y perfiles</li>
              <li>• Áreas</li>
              <li>• Mailboxes (configuración de correo)</li>
              <li>• Reglas MCI</li>
              <li>• Empresas</li>
            </ul>
          </div>

          {!confirming ? (
            <Button
              variant="danger"
              onClick={() => setConfirming(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar todos los datos
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  ¿Estás seguro? Escribe "ELIMINAR" para confirmar.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="danger"
                  onClick={handleCleanup}
                  isLoading={loading}
                >
                  Confirmar eliminación
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => { setConfirming(false); setResult(null) }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {result && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <h5 className="text-xs font-semibold text-green-800 dark:text-green-200 mb-2">Resultado:</h5>
              <pre className="text-xs text-green-700 dark:text-green-300 overflow-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}